import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useQuery } from "@/lib/useQuery";
import type { Doc } from "@/lib/types";
import { shortDate, titleCase } from "@/lib/format";
import { Button, Card, PageHeader, Loading, Empty, Tag, Modal, Field, toast, AiTag } from "@/components/ui";
import { useMe } from "@/app/MeContext";
import { renderMarkdown } from "@/lib/markdown";

const CATS = [["standard_policy", "Policy"], ["template", "Template"], ["agreement", "Agreement"], ["contract", "Contract"], ["upload", "Upload"]] as const;

export function DocsLibraryPage() {
  const me = useMe();
  const canEdit = me.permissions.editStandardDocs;
  const [params, setParams] = useSearchParams();
  const { data, loading, refetch } = useQuery<Doc[]>("docs-library", () => api.get<Doc[]>("/documents?library=1"));
  const [open, setOpen] = useState<Doc | null>(null);
  const [editing, setEditing] = useState<Doc | "new" | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [cat, setCat] = useState("template");
  useEffect(() => {
    const id = params.get("open");
    if (id) api.get<Doc>(`/documents/${id}`).then(setOpen).catch(() => {});
  }, [params]);
  const upload = async (file: File) => {
    const form = new FormData(); form.append("file", file); form.append("category", cat);
    try { await api.upload("/documents/upload", form); refetch(); toast(`Uploaded ${file.name}`); } catch (e) { toast((e as Error).message, true); }
  };
  const latest = (data ?? []).filter((d) => !(data ?? []).some((o) => o.parentId === d.id));
  return (
    <>
      <PageHeader title="Docs library" sub="Ownership model, policies and templates">
        {canEdit && <>
          <select className="select" style={{ width: 140 }} value={cat} onChange={(e) => setCat(e.target.value)}>{CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <input ref={fileRef} type="file" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          <Button onClick={() => fileRef.current?.click()}><Upload size={14} /> Upload file</Button>
          <Button variant="primary" onClick={() => setEditing("new")}>New text document</Button>
        </>}
      </PageHeader>
      <div className="page">
        <Card>
          {loading && !data ? <Loading /> : latest.length === 0 ? <Empty>The standard docs library is empty. Add the MSA, SOW, license agreement and data ownership terms so Claude can draft from them.</Empty> : (
            <table className="table">
              <thead><tr><th>Title</th><th>Category</th><th>Version</th><th>Updated</th><th></th></tr></thead>
              <tbody>
                {latest.map((d) => (
                  <tr key={d.id} className="click" onClick={() => setOpen(d)}>
                    <td style={{ fontWeight: 500 }} className="row gap-8">{d.title}{d.aiGenerated && <AiTag />}</td>
                    <td><Tag color="outline">{titleCase(d.category)}</Tag></td>
                    <td className="num muted">v{d.version}</td>
                    <td className="muted small">{shortDate(d.updatedAt)}</td>
                    <td className="right" onClick={(e) => e.stopPropagation()}>
                      {d.key ? <a href={`/api/documents/${d.id}/file`} target="_blank" rel="noreferrer" className="small">Open</a> : canEdit && <button className="btn-link small" onClick={() => setEditing(d)}>New version</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
      {open && (
        <Modal title={<span>{open.title} <span className="mono xs muted">v{open.version}</span></span>} onClose={() => { setOpen(null); if (params.get("open")) setParams({}); }} wide>
          {open.body ? <div className="prose" dangerouslySetInnerHTML={{ __html: renderMarkdown(open.body) }} /> : open.key ? <a href={`/api/documents/${open.id}/file`} target="_blank" rel="noreferrer">Open file</a> : <span className="muted">Empty</span>}
        </Modal>
      )}
      {editing && <TextDocModal doc={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />}
    </>
  );
}

function TextDocModal({ doc, onClose, onSaved }: { doc: Doc | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ title: doc?.title ?? "", category: doc?.category ?? "template", body: doc?.body ?? "" });
  const save = async () => {
    try { await api.post("/documents", { ...f, accountId: null }); onSaved(); } catch (e) { toast((e as Error).message, true); }
  };
  return (
    <Modal title={doc ? `New version of ${doc.title}` : "New text document"} onClose={onClose} wide footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!f.title || !f.body} onClick={save}>Save{doc ? ` as v${doc.version + 1}` : ""}</Button></>}>
      <div className="form-grid">
        <Field label="Title"><input className="input" disabled={!!doc} value={f.title} onChange={(e) => setF((x) => ({ ...x, title: e.target.value }))} /></Field>
        <Field label="Category"><select className="select" value={f.category} onChange={(e) => setF((x) => ({ ...x, category: e.target.value }))}>{CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
      </div>
      <Field label="Body (markdown)"><textarea className="textarea" style={{ minHeight: 320, fontFamily: "var(--mono)", fontSize: 12 }} value={f.body} onChange={(e) => setF((x) => ({ ...x, body: e.target.value }))} /></Field>
    </Modal>
  );
}
